import React from 'react';
import { useToolStore } from '../../store/useToolStore';
import { TOOLS, ToolControl, ToolSection } from '../../config/toolDefinitions';

// Custom Widget Imports
import { LandTypology } from '../widgets/LandTypology';
import { GeoGrounding } from '../widgets/GeoGrounding';
import { MaterialOverride } from '../widgets/MaterialOverride';
import { IdentityLocker } from '../widgets/IdentityLocker';
import { LandscapeIndex } from '../widgets/LandscapeIndex';

const WIDGET_REGISTRY: Record<string, React.FC> = {
    'LandTypology': LandTypology,
    'GeoGrounding': GeoGrounding,
    'MaterialOverride': MaterialOverride,
    'IdentityLocker': IdentityLocker,
    'LandscapeIndex': LandscapeIndex
};

export const UniversalToolbar: React.FC = () => {
    const activeToolId = useToolStore((state) => state.activeTool);
    const store = useToolStore(); // Access full store for getter/setter
    const setGenericParam = useToolStore((state) => state.setGenericToolParam);

    const config = TOOLS[activeToolId];

    if (!config) {
        return <div className="p-4 text-zinc-500">No configuration for {activeToolId}</div>;
    }

    // Helper to get value from dot notation string
    const getValue = (path: string) => {
        const keys = path.split('.');
        let current: any = store;
        for (const key of keys) {
            if (current === undefined || current === null) return undefined;
            current = current[key];
        }
        return current;
    };

    const renderControl = (control: ToolControl) => {
        if (control.showIf) {
            const condValue = getValue(control.showIf.key);
            if (condValue !== control.showIf.value) return null;
        }

        const value = getValue(control.key);

        switch (control.type) {
            case 'custom':
                if (control.component && WIDGET_REGISTRY[control.component]) {
                    const Widget = WIDGET_REGISTRY[control.component];
                    return <Widget key={control.key} />;
                }
                return <div key={control.key} className="text-red-500 text-xs">Missing Widget: {control.component}</div>;

            case 'slider':
                return (
                    <div key={control.key} className="space-y-2">
                        <div className="flex justify-between text-xs text-zinc-400">
                            <span className="flex items-center gap-1">
                                {control.icon && <control.icon size={12} />}
                                {control.label}
                            </span>
                            <span className="text-blue-400">{value}%</span>
                        </div>
                        <input
                            type="range"
                            min={control.min}
                            max={control.max}
                            step={control.step || 1}
                            value={value || 0}
                            onChange={(e) => setGenericParam(control.key, Number(e.target.value))}
                            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                        />
                        {control.description && <div className="text-center text-[10px] text-gray-500">{control.description}</div>}
                    </div>
                );

            case 'select':
                return (
                    <div key={control.key} className="space-y-2">
                        <label className="text-xs text-gray-400">{control.label}</label>
                        <select
                            value={value}
                            onChange={(e) => setGenericParam(control.key, e.target.value)}
                            className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-xs text-gray-300 focus:outline-none focus:border-blue-500"
                        >
                            {control.options?.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                );

            case 'segment':
                return (
                    <div key={control.key} className="space-y-2">
                        <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider">{control.label}</h3>
                        <div className="bg-gray-800 p-2 rounded-lg grid grid-cols-2 gap-2">
                            {control.options?.map((opt) => (
                                <button
                                    key={opt.value as string}
                                    onClick={() => setGenericParam(control.key, opt.value)}
                                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-all text-center border ${value === opt.value ? 'bg-indigo-600/20 border-indigo-500 text-indigo-200' : 'bg-gray-700 border-transparent text-gray-400 hover:bg-gray-600'}`}
                                >
                                    {/* {opt.icon && <opt.icon size={12} className="inline mr-1"/>} */}
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                );

            case 'multiselect':
                // value is array
                const isSelected = (val: string) => Array.isArray(value) && value.includes(val);
                const toggle = (val: string) => {
                    const current = Array.isArray(value) ? value : [];
                    const newValue = current.includes(val)
                        ? current.filter((v: any) => v !== val)
                        : [...current, val];
                    setGenericParam(control.key, newValue);
                };

                return (
                    <div key={control.key} className="bg-gray-800 p-4 rounded-lg space-y-3">
                        <label className="text-xs text-gray-400">{control.label}</label>
                        <div className="grid grid-cols-3 gap-2">
                            {control.options?.map((option) => (
                                <button
                                    key={option.value as string}
                                    onClick={() => toggle(option.value as string)}
                                    className={`flex flex-col items-center justify-center p-2 rounded bg-gray-700 hover:bg-gray-600 transition-colors ${isSelected(option.value as string) ? 'ring-2 ring-blue-500' : ''}`}
                                >
                                    {option.icon && <option.icon size={16} className="mb-1 text-gray-300" />}
                                    <span className="text-[10px] text-gray-400">{option.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                );

            case 'switch':
                return (
                    <div key={control.key} className="flex items-center justify-between bg-gray-700 p-2 rounded">
                        <div className="flex items-center space-x-2">
                            {control.icon && <control.icon size={14} className={value ? "text-orange-400" : "text-gray-400"} />}
                            <span className="text-xs text-gray-300">{control.label}</span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={!!value}
                                onChange={(e) => setGenericParam(control.key, e.target.checked)}
                                className="sr-only peer"
                            />
                            <div className="w-9 h-5 bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                    </div>
                );
            case 'text':
                return (
                    <div key={control.key} className="space-y-2">
                        <label className="text-xs text-gray-400">{control.label}</label>
                        <input
                            type="text"
                            placeholder={control.placeholder}
                            value={value || ''}
                            onChange={(e) => setGenericParam(control.key, e.target.value)}
                            className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-xs text-gray-300 focus:outline-none focus:border-blue-500"
                        />
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="space-y-8 animate-fadeIn">
            {/* <h2 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">{config.label}</h2> */}
            {config.sections.map(section => (
                <div key={section.id} className="space-y-4">
                    {section.label && (
                        <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-800 pb-2">
                            {section.label}
                        </h3>
                    )}
                    <div className="space-y-4">
                        {section.controls.map(control => renderControl(control))}
                    </div>
                </div>
            ))}
        </div>
    );
};
